import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Wallet,
  CreditCard,
  Plus,
  TrendingUp,
  AlertTriangle,
  FileText,
  Receipt,
  IndianRupee,
  Loader2,
  Info,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import UniversalPaymentCheckout from '../../components/UniversalPaymentCheckout';

const AdWalletPage = () => {
  const { apiCall, user } = useAuth();
  const { toast } = useToast();

  // State
  const [balance, setBalance] = useState(null);
  const [taxSummary, setTaxSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topUpDialog, setTopUpDialog] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [applyTds, setApplyTds] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Universal Payment Checkout state
  const [showPaymentCheckout, setShowPaymentCheckout] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [balanceData, taxData, txnData] = await Promise.all([
        apiCall('/api/ad-wallet/balance'),
        apiCall('/api/ad-wallet/tax-summary'),
        apiCall('/api/ad-wallet/transactions?limit=20'),
      ]);
      setBalance(balanceData);
      setTaxSummary(taxData);
      setTransactions(txnData.transactions || []);
    } catch (error) {
      console.error('Failed to fetch wallet data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load wallet data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [apiCall, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const calculateTopUp = () => {
    const amount = parseFloat(topUpAmount) || 0;
    const gst = amount * 0.18;
    const gross = amount + gst;
    // TDS is 2% of BASE amount (not gross), deducted by vendor if eligible
    const tds = applyTds ? amount * 0.02 : 0;
    const netPayable = gross - tds;
    // Credits added = BASE amount only (GST is tax, not credit)
    return { amount, gst, gross, tds, netPayable, creditsAdded: amount };
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Use Universal Payment Checkout instead of inline Razorpay
  const handleTopUp = () => {
    const calc = calculateTopUp();
    if (calc.amount < 100) {
      toast({
        title: 'Error',
        description: 'Minimum top-up amount is ₹100',
        variant: 'destructive',
      });
      return;
    }
    
    // Close the amount dialog and open Universal Checkout
    setTopUpDialog(false);
    setShowPaymentCheckout(true);
  };
  
  // Handle successful payment from Universal Checkout
  const handlePaymentSuccess = (result) => {
    toast({
      title: 'Top-up Successful',
      description: `₹${result.credits_added?.toFixed(2) || topUpAmount} added to your wallet.${result.gst_voucher_number ? ` GST Voucher: ${result.gst_voucher_number}` : ''}`,
    });
    setShowPaymentCheckout(false);
    setTopUpAmount('');
    setApplyTds(false);
    fetchData();
  };
  
  // Handle payment error
  const handlePaymentError = (error) => {
    toast({
      title: 'Top-up Failed',
      description: error.description || 'Payment was not successful',
      variant: 'destructive',
    });
  };

  // Legacy inline Razorpay (kept for reference, but no longer used)
  const handleTopUpLegacy = async () => {
    const calc = calculateTopUp();
    if (calc.amount < 100) {
      toast({
        title: 'Error',
        description: 'Minimum top-up amount is ₹100',
        variant: 'destructive',
      });
      return;
    }

    try {
      setProcessing(true);
      
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load Razorpay. Please check your internet connection.');
      }

      // Create order on backend
      const orderResponse = await apiCall('/api/razorpay/create-order', {
        method: 'POST',
        body: JSON.stringify({
          amount: calc.amount,
          apply_tds: applyTds,
        }),
      });

      if (!orderResponse.success) {
        throw new Error(orderResponse.message || 'Failed to create order');
      }

      // Open Razorpay checkout
      const options = {
        key: orderResponse.key_id,
        amount: orderResponse.amount,
        currency: orderResponse.currency,
        name: 'Aniyor Marketplace',
        description: 'Ad Wallet Top-Up',
        order_id: orderResponse.order_id,
        prefill: {
          name: orderResponse.prefill?.name || '',
          email: orderResponse.prefill?.email || '',
        },
        theme: {
          color: '#6366f1',
        },
        handler: async function (response) {
          // Verify payment on backend
          try {
            const verifyResponse = await apiCall('/api/razorpay/verify-payment', {
              method: 'POST',
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                apply_tds: applyTds,
              }),
            });

            toast({
              title: 'Top-up Successful',
              description: `₹${verifyResponse.credits_added?.toFixed(2)} added to your wallet. GST Voucher: ${verifyResponse.gst_voucher_number}`,
            });
            setTopUpDialog(false);
            setTopUpAmount('');
            setApplyTds(false);
            fetchData();
          } catch (verifyError) {
            toast({
              title: 'Verification Issue',
              description: verifyError.message || 'Payment received but verification pending. Please contact support if credits are not added.',
              variant: 'destructive',
            });
          }
        },
        modal: {
          ondismiss: function () {
            setProcessing(false);
            toast({
              title: 'Payment Cancelled',
              description: 'You closed the payment window.',
            });
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        toast({
          title: 'Payment Failed',
          description: response.error?.description || 'Payment was not successful. Please try again.',
          variant: 'destructive',
        });
        setProcessing(false);
      });
      razorpay.open();

    } catch (error) {
      toast({
        title: 'Top-up Failed',
        description: error.message || 'Failed to process top-up',
        variant: 'destructive',
      });
      setProcessing(false);
    }
  };

  const getTransactionBadge = (type) => {
    const badges = {
      credit_purchase: { variant: 'default', label: 'Credit Purchase' },
      trade_credit_grant: { variant: 'secondary', label: 'Trade Credit' },
      ad_spend_cpc: { variant: 'destructive', label: 'CPC Spend' },
      ad_spend_cpm: { variant: 'destructive', label: 'CPM Spend' },
      refund: { variant: 'outline', label: 'Refund' },
    };
    return badges[type] || { variant: 'outline', label: type };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  const calc = calculateTopUp();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Wallet className="h-6 w-6" />
              Ad Wallet
            </h1>
            <p className="text-muted-foreground">
              Manage your advertising credits and view transaction history
            </p>
          </div>
          <Button onClick={() => setTopUpDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Credits
          </Button>
        </div>

        {/* Balance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Available Balance</p>
                  <p className="text-3xl font-bold">₹{balance?.available_balance?.toFixed(2) || '0.00'}</p>
                </div>
                <Wallet className="h-10 w-10 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Trade Credit</p>
                  <p className="text-3xl font-bold">₹{balance?.trade_credit_remaining?.toFixed(2) || '0.00'}</p>
                </div>
                <CreditCard className="h-10 w-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">FY Total Spend</p>
                  <p className="text-3xl font-bold">₹{balance?.fy_total_spend?.toFixed(2) || '0.00'}</p>
                </div>
                <TrendingUp className="h-10 w-10 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card className={balance?.threshold_status === 'approaching' ? 'border-yellow-500' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">TDS Threshold</p>
                  <p className="text-lg font-bold">
                    {balance?.threshold_status === 'above_threshold' && (
                      <Badge variant="default" className="bg-green-500">Above ₹1L</Badge>
                    )}
                    {balance?.threshold_status === 'approaching' && (
                      <Badge variant="warning" className="bg-yellow-500">Approaching</Badge>
                    )}
                    {balance?.threshold_status === 'below_threshold' && (
                      <Badge variant="outline">Below ₹1L</Badge>
                    )}
                  </p>
                </div>
                {balance?.threshold_status === 'approaching' ? (
                  <AlertTriangle className="h-10 w-10 text-yellow-500" />
                ) : (
                  <IndianRupee className="h-10 w-10 text-muted-foreground" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* TDS Alert */}
        {balance?.threshold_status === 'approaching' && (
          <Card className="border-yellow-300 bg-yellow-50">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-yellow-800">TDS Readiness Alert</h4>
                  <p className="text-sm text-yellow-700">
                    Your annual ad spend is approaching ₹1,00,000. Please ensure your TAN is verified
                    to avail TDS benefits on future top-ups.
                  </p>
                  <Button variant="link" className="text-yellow-800 p-0 h-auto mt-1">
                    Verify TAN Now →
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="transactions">
          <TabsList>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="tax">Tax Summary</TabsTrigger>
          </TabsList>

          <TabsContent value="transactions" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Transaction History</CardTitle>
                <CardDescription>Your recent wallet transactions</CardDescription>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground">
                    <Receipt className="h-12 w-12 mx-auto mb-4" />
                    <p>No transactions yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((txn) => {
                        const badge = getTransactionBadge(txn.transaction_type);
                        return (
                          <TableRow key={txn.id}>
                            <TableCell className="text-sm text-muted-foreground">
                              {new Date(txn.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Badge variant={badge.variant}>{badge.label}</Badge>
                            </TableCell>
                            <TableCell>{txn.description}</TableCell>
                            <TableCell className={`text-right font-medium ${txn.net_amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {txn.net_amount > 0 ? '+' : ''}₹{Math.abs(txn.net_amount).toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right">
                              ₹{txn.balance_after?.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tax" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    GST Summary ({taxSummary?.financial_year})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Base Amount</span>
                    <span className="font-medium">₹{taxSummary?.gst_summary?.total_base_amount?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">GST Paid (18%)</span>
                    <span className="font-medium">₹{taxSummary?.gst_summary?.total_gst_paid?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-semibold">Total Gross</span>
                    <span className="font-bold">₹{taxSummary?.gst_summary?.total_gross?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {taxSummary?.gst_summary?.voucher_count || 0} Receipt Vouchers Generated
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    TDS Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">TAN Verified</span>
                    {taxSummary?.tds_summary?.has_verified_tan ? (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Verified
                      </Badge>
                    ) : (
                      <Badge variant="outline">Not Verified</Badge>
                    )}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Annual Spend</span>
                    <span className="font-medium">₹{taxSummary?.tds_summary?.annual_spend?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">TDS Threshold</span>
                    <span className="font-medium">₹{taxSummary?.tds_summary?.threshold?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total TDS Deducted</span>
                    <span className="font-medium">₹{taxSummary?.tds_summary?.total_tds_deducted?.toFixed(2) || '0.00'}</span>
                  </div>
                  {taxSummary?.tds_summary?.pending_verification > 0 && (
                    <div className="p-3 bg-yellow-50 rounded-lg">
                      <p className="text-sm text-yellow-800 flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {taxSummary.tds_summary.pending_verification} Form 16A pending (₹{taxSummary.tds_summary.pending_amount?.toFixed(2)})
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Top-up Dialog */}
      <Dialog open={topUpDialog} onOpenChange={setTopUpDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Credits to Wallet</DialogTitle>
            <DialogDescription>
              Top-up your ad wallet to run campaigns
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount (₹)</Label>
              <Input
                id="amount"
                type="number"
                placeholder="Enter amount (min ₹100)"
                value={topUpAmount}
                onChange={(e) => setTopUpAmount(e.target.value)}
                min={100}
              />
            </div>

            {/* Breakdown */}
            {calc.amount >= 100 && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Base Amount</span>
                  <span>₹{calc.amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>GST (18%)</span>
                  <span>₹{calc.gst.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm font-medium border-t pt-2">
                  <span>Gross Amount</span>
                  <span>₹{calc.gross.toFixed(2)}</span>
                </div>
                {applyTds && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>TDS Deduction (2%)</span>
                    <span>-₹{calc.tds.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>You Pay</span>
                  <span>₹{calc.netPayable.toFixed(2)}</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  Credits Added: ₹{calc.creditsAdded.toFixed(2)}
                </div>
              </div>
            )}

            {/* TDS Option */}
            {balance?.tds_enabled && (
              <div className="flex items-center space-x-2 p-3 bg-green-50 rounded-lg">
                <Checkbox
                  id="tds"
                  checked={applyTds}
                  onCheckedChange={setApplyTds}
                />
                <Label htmlFor="tds" className="text-sm">
                  Deduct TDS (2%) - You are eligible for TDS deduction
                </Label>
              </div>
            )}

            {!balance?.tds_enabled && (
              <div className="p-3 bg-gray-50 rounded-lg text-sm text-muted-foreground flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5" />
                <span>
                  TDS deduction is available after your annual spend crosses ₹1,00,000 and TAN is verified.
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopUpDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleTopUp} disabled={processing || calc.amount < 100}>
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pay ₹{calc.netPayable.toFixed(2)}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Universal Payment Checkout Modal */}
      <UniversalPaymentCheckout
        isOpen={showPaymentCheckout}
        onClose={() => {
          setShowPaymentCheckout(false);
          setTopUpAmount('');
          setApplyTds(false);
        }}
        userContext={{
          name: user?.name,
          email: user?.email,
          mobile: user?.mobile || user?.phone,
          gstin: user?.gstin || user?.vendor_profile?.gstin,
          razorpayCustomerId: user?.razorpay_customer_id
        }}
        paymentContext={{
          type: 'AD_CREDIT',
          amount: parseFloat(topUpAmount) || 0,
          metaData: {
            applyTds: applyTds
          }
        }}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
      />
    </DashboardLayout>
  );
};

export default AdWalletPage;
